import { apiFetch } from "./apiFetch";

// Admin customer/order access management — who can view a customer's
// orders and who receives order emails. See backend docs section 3
// ("Admin — managing who can access a customer's orders") for the full
// request/response contract.

export const fetchCustomers = ({ search, page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search && search.trim()) params.set("search", search.trim());
  return apiFetch(`/admin/customers?${params}`);
};

export const fetchCustomerUsers = (customerId) =>
  apiFetch(`/admin/customers/${customerId}/users`);

export const fetchCustomerOrders = (customerId, { page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  return apiFetch(`/admin/customers/${customerId}/orders?${params}`);
};

// tests[] on the returned order is paginated - order-level fields
// (order_id, order_number, order_name, processed_at) stay constant across
// pages. data.pagination sits alongside orders, not nested inside it.
export const fetchAdminOrder = (orderId, { page = 1, limit = 10 } = {}) => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  return apiFetch(`/admin/orders/${orderId}?${params}`);
};

export const fetchOrderAccess = (orderId) => apiFetch(`/admin/orders/${orderId}/access`);

// Admin/operator equivalent of fetchPatientReportLink/fetchTherapistReportLink
// - unscoped by ownership, since admin can already see any order. Resolves a
// fresh 5-minute presigned S3 URL on each call - never cached.
export const fetchAdminReportLink = (labTestId) => apiFetch(`/admin/reports/${labTestId}/link`);

// testIds: string[] (uuid) - required, non-empty, must all belong to the
// order. Additive: calling again for the same email with different testIds
// adds to their existing grant, and for a testId already granted, upserts
// just that test's hasAccess/receivesEmails, rather than replacing the
// whole grant.
export const grantOrderAccess = (orderId, { email, hasAccess, receivesEmails, testIds }) =>
  apiFetch(`/admin/orders/${orderId}/access`, {
    method: "POST",
    body: JSON.stringify({ email, hasAccess, receivesEmails, testIds }),
  });

// patch: subset of { hasAccess, receivesEmails, scope, orderIds } - only send what's changing.
export const updateUserAccess = (customerId, userId, patch) =>
  apiFetch(`/admin/customers/${customerId}/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

// Removes one order from a scope:'specific' grant - leaves the rest of the
// grant untouched. testIds: optional string[] (uuid) - non-empty removes
// only those tests (partial revoke), leaving the rest of the order's grant
// intact; omitted/empty removes every test on that order (full removal).
export const removeUserOrderAccess = (customerId, userId, orderId, testIds) => {
  const query =
    Array.isArray(testIds) && testIds.length > 0
      ? `?testIds=${testIds.map(encodeURIComponent).join(",")}`
      : "";
  return apiFetch(`/admin/customers/${customerId}/users/${userId}/orders/${orderId}${query}`, {
    method: "DELETE",
  });
};

// Hard delete - removes the grant entirely, no history kept.
export const removeUserAccess = (customerId, userId) =>
  apiFetch(`/admin/customers/${customerId}/users/${userId}`, { method: "DELETE" });

// Irreversible - wipes this customer's portal/PII data (form submissions, lab
// results, QR codes, shipping labels, report PDFs, non-default access grants,
// and resets their login state). Orders, the Shopify customer record, and
// billing/shipping addresses are kept untouched. `reason` is required by the
// backend (400 if blank).
export const deleteCustomerData = (customerId, reason) =>
  apiFetch(`/admin/customers/${customerId}/delete-data`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
